"use client";

import { useMemo, useState } from "react";
import OptionContent, { OptionImage } from "./OptionContent";
import { CheckCircle2 } from "lucide-react";
import type {
  PublicProductOption,
  PublicProductOptionGroup,
  PublicProductOptionSection,
} from "@/types/store";
import type { CompositionSelection } from "@/types/food-configuration";
import { foodFlavorCounts } from "@/lib/food-configuration";
import { formatCurrency } from "@/lib/order";

type VisibleSection = {
  section: PublicProductOptionSection | null;
  options: PublicProductOption[];
};

function normalizeSearch(text: string) {
  return text
    .normalize("NFD")
    .replace(/[\\u0300-\\u036f]/g, "")
    .toLocaleLowerCase("pt-BR");
}

export default function FoodCompositionSelector({
  group,
  value,
  variationId,
  onChange,
  locked = false,
}: {
  group: PublicProductOptionGroup;
  value: CompositionSelection[];
  variationId: number | null;
  onChange: (value: CompositionSelection[], count: number) => void;
  locked?: boolean;
}) {
  const counts = foodFlavorCounts(group, variationId);
  const [count, setCount] = useState(
    locked ? 1 : (counts[0] ?? 1),
  );
  const [search, setSearch] = useState("");
  const [activeSection, setActiveSection] = useState<number | "all">("all");

  const activeSections = useMemo(
    () =>
      (group.sections ?? [])
        .filter((section) => section.is_active)
        .sort(
          (left, right) =>
            left.position - right.position || left.id - right.id,
        ),
    [group.sections],
  );

  const visibleOptions = useMemo(() => {
    const query = normalizeSearch(search.trim());

    return group.options.filter((option) => {
      const selected = value.some(
        (entry) => entry.option_id === option.id,
      );

      if (locked && !selected) {
        return false;
      }

      const sectionMatches =
        activeSection === "all" ||
        option.section === activeSection ||
        selected;

      if (!sectionMatches) {
        return false;
      }

      if (!query) {
        return true;
      }

      return (
        normalizeSearch(option.name).includes(query) ||
        normalizeSearch(option.code ?? "").includes(query) ||
        normalizeSearch(option.description ?? "").includes(query) ||
        selected
      );
    });
  }, [
    activeSection,
    group.options,
    search,
    value,
  ]);

  const visibleSections = useMemo<VisibleSection[]>(() => {
    if (activeSections.length === 0) {
      return [
        {
          section: null,
          options: visibleOptions,
        },
      ];
    }

    const result: VisibleSection[] = [];

    for (const section of activeSections) {
      const options = visibleOptions.filter(
        (option) => option.section === section.id,
      );

      if (options.length > 0) {
        result.push({
          section,
          options,
        });
      }
    }

    const withoutSection = visibleOptions.filter(
      (option) =>
        option.section == null ||
        !activeSections.some(
          (section) => section.id === option.section,
        ),
    );

    if (withoutSection.length > 0) {
      result.push({
        section: null,
        options: withoutSection,
      });
    }

    return result;
  }, [
    activeSections,
    visibleOptions,
  ]);

  const fraction = count === 1 ? "1.0" : "0.5";

  function renderOption(option: PublicProductOption) {
    const selected = value.find(
      (entry) => entry.option_id === option.id,
    );

    const price = option.variation_rules?.find(
      (rule) => rule.variation === variationId,
    )?.full_price;

    const label = option.code
      ? `${option.code} · ${option.name}`
      : option.name;

    return (
      <div
        key={option.id}
        className={`relative rounded-xl border ${
          selected
            ? "border-blue-500 bg-blue-50"
            : "border-slate-200"
        }`}
      >
        {option.image_url && (
          <div className="absolute left-11 top-1/2 z-10 -translate-y-1/2">
            <OptionImage option={option} />
          </div>
        )}

        <button
          type="button"
          role="checkbox"
          aria-label={label}
          aria-checked={!!selected}
          disabled={
            locked ||
            !counts.includes(count) ||
            (!selected && value.length >= count)
          }
          onClick={() =>
            onChange(
              selected
                ? value.filter(
                    (entry) =>
                      entry.option_id !== option.id,
                  )
                : [
                    ...value,
                    {
                      option_id: option.id,
                      fraction,
                    },
                  ],
              count,
            )
          }
          className="flex min-h-16 w-full items-center gap-3 p-3 text-left text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-45"
        >
          <span
            aria-hidden="true"
            className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border ${
              selected
                ? "border-blue-600 bg-blue-600 text-white"
                : "border-slate-300 bg-white"
            }`}
          >
            {selected && <CheckCircle2 size={15} />}
          </span>

          {option.image_url && (
            <span
              aria-hidden="true"
              className="h-12 w-12 shrink-0"
            />
          )}

          <div className="min-w-0 flex-1">
            {option.code ? (
              <div className="mb-0.5 flex items-baseline gap-2">
                <span className="shrink-0 text-xs font-black text-blue-700">
                  {option.code}
                </span>
                <span className="truncate font-black text-slate-900">
                  {option.name}
                </span>
              </div>
            ) : (
              <OptionContent option={option} />
            )}

            {option.code && option.description && (
              <p className="mt-0.5 line-clamp-2 text-xs font-medium leading-5 text-slate-500">
                {option.description}
              </p>
            )}
          </div>

          {price != null && (
            <span className="shrink-0 text-xs font-black text-slate-700 sm:text-sm">
              {formatCurrency(price)}
            </span>
          )}
        </button>
      </div>
    );
  }

  return (
    <div className="mt-3 space-y-4">
      {counts.length === 0 && (
        <p
          role="alert"
          className="text-sm text-amber-700"
        >
          Nenhuma composição de 1 ou 2 sabores está disponível neste tamanho.
        </p>
      )}

      <label className="block text-sm font-semibold">
        Quantidade de sabores

        <select
          className="form-input mt-1"
          disabled={locked || counts.length === 0}
          value={count}
          onChange={(event) => {
            const nextCount = Number(
              event.target.value,
            );

            setCount(nextCount);

            onChange(
              value
                .slice(0, nextCount)
                .map((entry) => ({
                  ...entry,
                  fraction:
                    nextCount === 1
                      ? "1.0"
                      : "0.5",
                })),
              nextCount,
            );
          }}
        >
          {counts.map((availableCount) => (
            <option
              key={availableCount}
              value={availableCount}
            >
              {availableCount === 1
                ? "1 sabor — Inteira"
                : "2 sabores — Meia a meia"}
            </option>
          ))}
        </select>
      </label>

      <p className="text-xs text-slate-500">
        Selecione exatamente {count}{" "}
        {count === 1 ? "sabor" : "sabores"}.
      </p>

      {!locked && activeSections.length > 0 && (
        <>
          <label className="block text-sm font-semibold sm:hidden">
            Categoria

            <select
              className="form-input mt-1 w-full"
              value={activeSection}
              onChange={(event) =>
                setActiveSection(
                  event.target.value === "all"
                    ? "all"
                    : Number(event.target.value),
                )
              }
            >
              <option value="all">Todos</option>

              {activeSections.map((section) => (
                <option
                  key={section.id}
                  value={section.id}
                >
                  {section.name}
                </option>
              ))}
            </select>
          </label>

          <div className="-mx-1 hidden gap-2 overflow-x-auto px-1 pb-1 sm:flex">
            <button
              type="button"
              onClick={() => setActiveSection("all")}
              className={`shrink-0 rounded-full border px-3 py-2 text-xs font-black transition ${
                activeSection === "all"
                  ? "border-blue-600 bg-blue-600 text-white"
                  : "border-slate-200 bg-white text-slate-600"
              }`}
            >
              Todos
            </button>

            {activeSections.map((section) => (
              <button
                key={section.id}
                type="button"
                onClick={() => setActiveSection(section.id)}
                className={`shrink-0 rounded-full border px-3 py-2 text-xs font-black transition ${
                  activeSection === section.id
                    ? "border-blue-600 bg-blue-600 text-white"
                    : "border-slate-200 bg-white text-slate-600"
                }`}
              >
                {section.name}
              </button>
            ))}
          </div>
        </>
      )}

      {!locked && group.options.length > 12 && (
        <label className="block text-sm font-semibold">
          Buscar em {group.name}

          <input
            type="search"
            value={search}
            onChange={(event) =>
              setSearch(event.target.value)
            }
            placeholder="Digite nome, código ou ingrediente"
            className="form-input mt-1 w-full"
          />
        </label>
      )}

      <div
        className={
          group.options.length > 12
            ? "max-h-[32rem] space-y-5 overflow-y-auto overscroll-contain pr-1"
            : "space-y-5"
        }
      >
        {visibleOptions.length === 0 && (
          <p className="p-3 text-sm text-slate-500">
            Nenhuma opção encontrada.
          </p>
        )}

        {visibleSections.map(
          ({ section, options }) => (
            <section
              key={
                section
                  ? `section-${section.id}`
                  : "section-none"
              }
              className="space-y-2"
            >
              {section && (
                <div className="sticky top-0 z-10 border-b border-slate-100 bg-white/95 py-2 backdrop-blur">
                  <h4 className="text-xs font-black uppercase tracking-[0.14em] text-slate-500">
                    {section.name}
                  </h4>
                </div>
              )}

              <div className="space-y-2">
                {options.map(renderOption)}
              </div>
            </section>
          ),
        )}
      </div>

      {!locked && !group.required && value.length > 0 && (
        <button
          type="button"
          className="text-sm text-blue-700"
          onClick={() => onChange([], 0)}
        >
          Limpar escolhas
        </button>
      )}
    </div>
  );
}
